import { useState, type ReactNode } from 'react'
import { exportExcel, exportPdf, type ExportTable } from './reportExport'
import WorkbookExport from './WorkbookExport'

/**
 * The count line and the PDF and Excel buttons above a report's table. The
 * report supplies the table to write and a file name stem; the extension
 * follows the format.
 */
export default function ExportBar({
  count,
  loading,
  disabled,
  fileStem,
  table,
  children,
}: {
  count?: string
  loading: boolean
  disabled: boolean
  fileStem: string
  table: () => ExportTable
  // Further actions beside the export buttons, such as Print.
  children?: ReactNode
}) {
  const [notice, setNotice] = useState<{ kind: 'done' | 'failed'; text: string } | null>(null)

  async function run(format: 'pdf' | 'xlsx') {
    const fileName = `${fileStem}.${format}`
    try {
      if (format === 'pdf') await exportPdf(table(), fileName)
      else await exportExcel(table(), fileName)
      setNotice({ kind: 'done', text: `Exported ${fileName}` })
    } catch {
      setNotice({ kind: 'failed', text: 'The export could not be created. Try again.' })
    }
  }

  const buttonClass = 'min-h-11 rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-50'
  return (
    <div className="flex flex-col gap-2 print:hidden">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <p role="status" className="text-small text-ink-muted">
          {notice?.kind === 'done' ? notice.text : loading ? 'Loading' : count}
        </p>
        <button type="button" disabled={disabled} onClick={() => run('pdf')} className={buttonClass}>
          Export PDF
        </button>
        <button type="button" disabled={disabled} onClick={() => run('xlsx')} className={buttonClass}>
          Export Excel
        </button>
        <WorkbookExport />
        {children}
      </div>
      {notice?.kind === 'failed' && (
        <p role="alert" className="text-small text-error">
          {notice.text}
        </p>
      )}
    </div>
  )
}
